import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import React, { useEffect, useState } from 'react';
import { getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from '../../utils/dateUtils';
import { mergeFormDataWithStatusExtras } from '../../utils/applicationFormPayload';
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";

import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import { tbTestSchema, formatSSN, formatName } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import PropTypes from 'prop-types';

const YesNoRadio = ({ name, label }) => {
  const { register } = useFormContext();
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors rounded-lg px-2">
      <span className="text-sm font-medium text-gray-700 w-2/3">{label}</span>
      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer group">
          <input
            type="radio"
            value="Yes"
            {...register(name)}
            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 transition-all"
          />
          <span className="text-sm text-gray-600 group-hover:text-blue-600 transition-colors">
            Yes
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer group">
          <input
            type="radio"
            value="No"
            {...register(name)}
            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 transition-all"
          />
          <span className="text-sm text-gray-600 group-hover:text-blue-600 transition-colors">
            No
          </span>
        </label>
      </div>
    </div>
  );
};

YesNoRadio.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};


const TbTest = ({ positionName, onClose }) => {
  const [testId, setTestId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const payload = {
        ...merged,
        hadTuberculosis: merged.hadTuberculosis || "No",
        hadBcgVaccine: merged.hadBcgVaccine || "No",
        hadPositiveTbSkinTest: merged.hadPositiveTbSkinTest || "No",
        hasProductiveCough: merged.hasProductiveCough || "No",
        hasNightSweats: merged.hasNightSweats || "No",
        hasFever: merged.hasFever || "No",
        hasWeightLoss: merged.hasWeightLoss || "No",
        hasLossOfAppetite: merged.hasLossOfAppetite || "No",
        applicantSignedAt: toIsoDate(merged.applicantSignedAt),
        reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt),
        datePlaced: merged.datePlaced ? toIsoDate(merged.datePlaced) : "",
        dateRead: merged.dateRead ? toIsoDate(merged.dateRead) : "",
        expirationDate: merged.expirationDate ? toIsoDate(merged.expirationDate) : "",
        flag: actualFlag,
      };

      // Clean up empty enum fields - remove them if they're empty strings
      // Backend expects these to be either valid enum values or omitted entirely
      if (payload.site === "" || payload.site === null) {
        delete payload.site;
      }
      if (payload.ppdResult === "" || payload.ppdResult === null) {
        delete payload.ppdResult;
      }

      // Clean up other empty optional fields
      if (payload.datePlaced === "") delete payload.datePlaced;
      if (payload.dateRead === "") delete payload.dateRead;
      if (payload.expirationDate === "") delete payload.expirationDate;

      if (testId) {
        payload.testId = testId;
      }

      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }
      let response;
      if (testId) {
        response = await request(`/tb-tests/${testId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/tb-tests', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "TB Test & Questionnaire processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const methods = useForm({
    resolver: zodResolver(tbTestSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      socialSecurityNumber: "",
      hadTuberculosis: "No",
      hadBcgVaccine: "No",
      hadPositiveTbSkinTest: "No",
      hasProductiveCough: "No",
      hasNightSweats: "No",
      hasFever: "No",
      hasWeightLoss: "No",
      hasLossOfAppetite: "No",
      applicantSignedAt: "",
      applicantSignedByUserId: 1,
      reviewedBySignedBy: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      positionAppliedFor: positionName || "",
      healthcareProfessionalName: "",
      datePlaced: "",
      site: "",
      dateRead: "",
      indurationMm: 0,
      lotNumber: "",
      expirationDate: "",
      ppdResult: "Negative",
      administeredByName: "",
      administeredByTitle: "",
      resultsReadByName: "",
      resultsReadByTitle: ""
    },
    mode: "onChange"
  });

  const { handleSubmit, setValue, watch, register, reset } = methods;

  const {
    targetUserId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    signerName,
    handleSignatureApply,
    handleStatusAction,
    handleSave,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId
  } = useApplicationFormLogic({
    methods,
    applicantNameField: null, // Disable auto-update of name on signature
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminNameField: "reviewedBySignedBy",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchTbTest = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !testId) return;

      try {
        if (!actualFromId && !targetUserId && !testId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              socialSecurityNumber: currentUserData.ssn || "",
              positionAppliedFor: positionName || "",
              applicantSignedAt: "",
              hadTuberculosis: "No",
              hadBcgVaccine: "No",
              hadPositiveTbSkinTest: "No",
              hasProductiveCough: "No",
              hasNightSweats: "No",
              hasFever: "No",
              hasWeightLoss: "No",
              hasLossOfAppetite: "No"
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/tb-tests/${actualFromId}?userId=${targetUserId}`
            );
          } else {
            response = await request(`/tb-tests?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/tb-tests/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) {
              data = response.result[0];
            }
          } else if (response.result?.test) {
            data = response.result.test;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId, 10));
            }
          }
        }

        if (data) {
          setTestId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
            datePlaced: parseIsoDateToMDYUS(data.datePlaced) || "",
            dateRead: parseIsoDateToMDYUS(data.dateRead) || "",
            expirationDate: parseIsoDateToMDYUS(data.expirationDate) || "",
            comments: data.comments || "",
            socialSecurityNumber: data.socialSecurityNumber || (!isAdmin ? currentUserData.ssn : "") || "",
            hadTuberculosis: data.hadTuberculosis || "No",
            hadBcgVaccine: data.hadBcgVaccine || "No",
            hadPositiveTbSkinTest: data.hadPositiveTbSkinTest || "No",
            hasProductiveCough: data.hasProductiveCough || "No",
            hasNightSweats: data.hasNightSweats || "No",
            hasFever: data.hasFever || "No",
            hasWeightLoss: data.hasWeightLoss || "No",
            hasLossOfAppetite: data.hasLossOfAppetite || "No",
            ppdResult: data.ppdResult || "Negative"
          });
        }
      } catch (error) {
        console.error("Error fetching TB test:", error);
      }
    };

    fetchTbTest();
  }, [targetUserId, reset, currentUserData, isAdmin, testId, actualFromId, positionName]);


  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Tuberculosis (TB) Screening Questionnaire" />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit((d) => onSubmit(d, 'send_to_office'))}>
              <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />
              <div className="space-y-8">
                <fieldset disabled={isReadOnly} className="space-y-8">
                  {/* Employee Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <EvaluationInput
                      name="employeeName"
                      label="Employee Name"
                      isRequired
                      onInput={(e) => { e.target.value = formatName(e.target.value); }}
                    />
                    <EvaluationInput
                      name="socialSecurityNumber"
                      label="Social Security Number"
                      placeholder="XXX-XX-XXXX"
                      maxLength={11}
                      onInput={(e) => {
                        e.target.value = formatSSN(e.target.value);
                      }}
                    />
                  </div>

                  <div className="space-y-8">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-600 py-1 px-3 rounded-md text-sm">
                          1
                        </span> {" "}
                        Screening Questions
                      </h3>
                      <div className="bg-white border boundary-gray-200 p-6 rounded-xl shadow-sm">
                        <YesNoRadio
                          name="hadTuberculosis"
                          label="Have you ever had Tuberculosis?"
                        />
                        <YesNoRadio
                          name="hadBcgVaccine"
                          label="Have you ever had the BCG Vaccine? (If yes, you must provide a Chest X-Ray report)"
                        />
                        <YesNoRadio
                          name="hadPositiveTbSkinTest"
                          label="Have you ever had a Positive TB Skin Test? (If yes, you must provide a Chest X-Ray report)"
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-600 py-1 px-3 rounded-md text-sm">
                          2
                        </span> {" "}
                        Symptoms Review
                      </h3>
                      <p className="text-sm text-gray-500 mb-4 italic">
                        Check 'Yes' if you have experienced any of the following
                        symptoms recently:
                      </p>
                      <div className="bg-white border boundary-gray-200 p-6 rounded-xl shadow-sm">
                        <YesNoRadio
                          name="hasProductiveCough"
                          label="Productive Cough"
                        />
                        <YesNoRadio name="hasNightSweats" label="Night Sweats" />
                        <YesNoRadio name="hasFever" label="Fever" />
                        <YesNoRadio name="hasWeightLoss" label="Weight Loss" />
                        <YesNoRadio
                          name="hasLossOfAppetite"
                          label="Loss of Appetite"
                        />
                      </div>
                    </div>
                  </div>


                  {/* Healthcare Professional Section */}
                  <div className="mt-12 border-t border-gray-200 pt-8">
                    <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                      <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6 uppercase tracking-wide">
                        TB Skin Test (PPD) {" "}
                        <span className="block text-xs font-normal text-gray-500 mt-1 normal-case">
                          To be completed by healthcare professional
                        </span>
                      </h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EvaluationInput
                          name="healthcareProfessionalName"
                          label="Healthcare Professional Name"
                        />

                        <div className="grid grid-cols-2 gap-4">
                          <InputDatePicker
                            formatSeparator="/"
                            name="datePlaced"
                            label="Date Placed"
                            max="9999-12-31"
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Site
                            </label>
                            <div className="flex gap-4 pt-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  value="Right"
                                  {...register("site")}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm">Right</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  value="Left"
                                  {...register("site")}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm">Left</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <InputDatePicker
                            formatSeparator="/"
                            name="dateRead"
                            label="Date Read"
                            max="9999-12-31"
                          />
                          <EvaluationInput
                            name="indurationMm"
                            label="Induration (mm)"
                            type="number"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <EvaluationInput name="lotNumber" label="Lot Number" />
                          <InputDatePicker
                            formatSeparator="/"
                            name="expirationDate"
                            label="Expiration Date"
                            max="9999-12-31" />
                        </div>

                        <div className="col-span-1 md:col-span-2 bg-white p-4 rounded-lg border border-gray-200">
                          <label className="block text-sm font-bold text-gray-800 mb-3">
                            PPD Result
                          </label>
                          <div className="flex gap-8">
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors border border-transparent hover:border-gray-100 flex-1">
                              <input
                                type="radio"
                                value="Negative"
                                {...register("ppdResult")}
                                className="w-5 h-5 text-gray-600"
                              />
                              <span className="font-medium text-gray-700">
                                Negative
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-red-50 p-2 rounded-lg transition-colors border border-transparent hover:border-red-100 flex-1">
                              <input
                                type="radio"
                                value="Positive"
                                {...register("ppdResult")}
                                className="w-5 h-5 text-red-600"
                              />
                              <span className="text-red-700 font-bold">Positive</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 pt-6 border-t border-gray-200">
                        <div className="bg-white p-4 rounded-lg border border-gray-100">
                          <h4 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wider">
                            Administered By:
                          </h4>
                          <div className="space-y-4">
                            <EvaluationInput
                              name="administeredByName"
                              label="Name"
                              placeholder="Administered by Name"
                            />
                            <EvaluationInput
                              name="administeredByTitle"
                              label="Title"
                              placeholder="Title"
                            />
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-100">
                          <h4 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wider">
                            Results Read By:
                          </h4>
                          <div className="space-y-4">
                            <EvaluationInput
                              name="resultsReadByName"
                              label="Name"
                              placeholder="Read by Name"
                            />
                            <EvaluationInput
                              name="resultsReadByTitle"
                              label="Title"
                              placeholder="Title"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
                <ApplicationFormFooter
                  page={1}
                  setPage={() => { }}
                  totalPages={1}
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  showCertification={false}
                  requireSignatures={true}
                  showSignatureSections={true}
                  applicantName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature") || ""}
                  applicantSignedAt={watch("applicantSignedAt") || ""}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature") || ""}
                  reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                  submitLabel="Submit & Print"
                  isReadOnly={isReadOnly}
                  onClose={closeForm}
                  currentStatus={currentStatus}
                />
              </div>
            </form>


          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default TbTest;

TbTest.propTypes = {
  positionName: PropTypes.string,
  onClose: PropTypes.func
};
